/**
 * PlayerColorChip
 *
 * A circular badge representing a player identity. Renders the player's
 * locked color; when colorBlindMode is true, overlays a pattern so color
 * is never the sole distinguishing signal (design principle #1).
 *
 * Patterns are rendered as tinted overlay Views with opacity/border tricks
 * rather than SVG (react-native-svg is not in the current dependency tree).
 * A future refactor can swap the pattern layer for SVG fills if that dep is
 * added — the public prop API will not change.
 */

import React from 'react';
import { View, Text, StyleSheet, ViewStyle, Platform } from 'react-native';
import { PLAYER_COLORS, PlayerSlot } from '../../colors';

export type ChipSize = 'sm' | 'md' | 'lg';

export interface PlayerColorChipProps {
  /** Which palette slot (1–9). */
  slot: PlayerSlot;
  /** Visual size of the chip. Defaults to 'md'. */
  size?: ChipSize;
  /** When true, renders a slot number label centered inside the chip. */
  showLabel?: boolean;
  /**
   * When true, overlays a pattern on top of the color fill so the chip is
   * distinguishable without relying on color alone (per CVD a11y principle).
   */
  colorBlindMode?: boolean;
  /** Optional additional style for the outer container View. */
  style?: ViewStyle;
}

const SIZE_MAP: Record<ChipSize, { diameter: number; fontSize: number; borderWidth: number }> = {
  sm: { diameter: 20, fontSize: 9, borderWidth: 1 },
  md: { diameter: 32, fontSize: 13, borderWidth: 1.5 },
  lg: { diameter: 48, fontSize: 18, borderWidth: 2 },
};

/**
 * Pattern overlay implementation without react-native-svg.
 * Each pattern is a lightweight View composition that approximates the
 * intended visual. These are intentionally simple for MVP; swap with SVG
 * fills for higher fidelity when react-native-svg is available.
 */
function PatternOverlay({
  pattern,
  size,
  color: _color,
}: {
  pattern: string;
  size: ChipSize;
  color: string;
}) {
  const { diameter } = SIZE_MAP[size];
  const overlayColor = 'rgba(0,0,0,0.25)';

  switch (pattern) {
    case 'diagonal':
    case 'reverse-diagonal': {
      // Diagonal stripes: three rotated thin bars
      const stripeWidth = Math.max(1, Math.round(diameter * 0.08));
      return (
        <View style={StyleSheet.absoluteFill} pointerEvents="none">
          {[-1, 0, 1, 2].map((i) => (
            <View
              key={i}
              style={{
                position: 'absolute',
                width: stripeWidth,
                height: diameter * 2,
                backgroundColor: overlayColor,
                top: -diameter * 0.5,
                left: diameter * 0.25 + i * (diameter * 0.25),
                transform: [{ rotate: pattern === 'reverse-diagonal' ? '-45deg' : '45deg' }],
              }}
            />
          ))}
        </View>
      );
    }

    case 'dots': {
      // 2×2 dot grid
      const dotR = Math.max(2, Math.round(diameter * 0.1));
      const positions = [
        { top: diameter * 0.28, left: diameter * 0.28 },
        { top: diameter * 0.28, left: diameter * 0.62 },
        { top: diameter * 0.62, left: diameter * 0.28 },
        { top: diameter * 0.62, left: diameter * 0.62 },
      ];
      return (
        <View style={StyleSheet.absoluteFill} pointerEvents="none">
          {positions.map((pos, i) => (
            <View
              key={i}
              style={{
                position: 'absolute',
                width: dotR * 2,
                height: dotR * 2,
                borderRadius: dotR,
                backgroundColor: overlayColor,
                top: pos.top - dotR,
                left: pos.left - dotR,
              }}
            />
          ))}
        </View>
      );
    }

    case 'crosshatch': {
      // Horizontal + vertical stripes combined
      const stripeW = Math.max(1, Math.round(diameter * 0.07));
      return (
        <View style={StyleSheet.absoluteFill} pointerEvents="none">
          {/* Horizontal bars */}
          {[-1, 0, 1].map((i) => (
            <View
              key={`h${i}`}
              style={{
                position: 'absolute',
                width: diameter,
                height: stripeW,
                backgroundColor: overlayColor,
                top: diameter * 0.5 + i * (diameter * 0.28) - stripeW / 2,
                left: 0,
              }}
            />
          ))}
          {/* Vertical bars */}
          {[-1, 0, 1].map((i) => (
            <View
              key={`v${i}`}
              style={{
                position: 'absolute',
                width: stripeW,
                height: diameter,
                backgroundColor: overlayColor,
                left: diameter * 0.5 + i * (diameter * 0.28) - stripeW / 2,
                top: 0,
              }}
            />
          ))}
        </View>
      );
    }

    case 'horizontal': {
      const stripeW = Math.max(1, Math.round(diameter * 0.08));
      return (
        <View style={StyleSheet.absoluteFill} pointerEvents="none">
          {[-1, 0, 1, 2].map((i) => (
            <View
              key={i}
              style={{
                position: 'absolute',
                width: diameter,
                height: stripeW,
                backgroundColor: overlayColor,
                top: diameter * 0.25 + i * (diameter * 0.2) - stripeW / 2,
                left: 0,
              }}
            />
          ))}
        </View>
      );
    }

    case 'vertical': {
      const stripeW = Math.max(1, Math.round(diameter * 0.08));
      return (
        <View style={StyleSheet.absoluteFill} pointerEvents="none">
          {[-1, 0, 1, 2].map((i) => (
            <View
              key={i}
              style={{
                position: 'absolute',
                width: stripeW,
                height: diameter,
                backgroundColor: overlayColor,
                left: diameter * 0.25 + i * (diameter * 0.2) - stripeW / 2,
                top: 0,
              }}
            />
          ))}
        </View>
      );
    }

    case 'rings': {
      return <View style={StyleSheet.absoluteFill} pointerEvents="none">
        {[0.35, 0.72].map(scale => <View key={scale} style={{
          position: 'absolute', left: diameter * (1 - scale) / 2, top: diameter * (1 - scale) / 2,
          width: diameter * scale, height: diameter * scale, borderRadius: diameter * scale / 2,
          borderWidth: Math.max(1, diameter * 0.06), borderColor: overlayColor,
        }} />)}
      </View>;
    }
    case 'checker': {
      return <View style={StyleSheet.absoluteFill} pointerEvents="none">
        {Array.from({ length: 16 }, (_, i) => i).filter(i => (Math.floor(i / 4) + i % 4) % 2 === 0)
          .map(i => <View key={i} style={{ position: 'absolute',
            top: Math.floor(i / 4) * diameter / 4, left: (i % 4) * diameter / 4,
            width: diameter / 4, height: diameter / 4, backgroundColor: overlayColor,
          }} />)}
      </View>;
    }
    // 'solid' — no overlay
    default:
      return null;
  }
}

export function PlayerColorChip({
  slot,
  size = 'md',
  showLabel = false,
  colorBlindMode = false,
  style,
}: PlayerColorChipProps) {
  const entry = PLAYER_COLORS[slot];
  const { diameter, fontSize, borderWidth } = SIZE_MAP[size];

  return (
    <View
      accessibilityLabel={`Player ${slot} color: ${entry.name}`}
      accessibilityRole="image"
      style={[
        {
          width: diameter,
          height: diameter,
          borderRadius: diameter / 2,
          backgroundColor: entry.hex,
          overflow: 'hidden',
          alignItems: 'center',
          justifyContent: 'center',
          borderWidth,
          borderColor: 'rgba(0,0,0,0.15)',
        },
        style,
      ]}
    >
      {colorBlindMode && (
        <PatternOverlay pattern={entry.pattern} size={size} color={entry.hex} />
      )}
      {showLabel && (
        <Text
          style={{
            fontSize,
            // Web's normal line box can snap the centered numeral to different
            // baselines inside a composited, fractionally positioned modal.
            // Use an explicit pixel line height; retain native font metrics.
            lineHeight: Platform.OS === 'web' ? Math.round(fontSize * 1.2) : undefined,
            fontWeight: '700',
            color: '#ffffff',
            // Drop shadow to keep text legible on any color fill
            textShadowColor: 'rgba(0,0,0,0.6)',
            textShadowOffset: { width: 0, height: 1 },
            textShadowRadius: 2,
            // Prevent label from capturing pointer events
            pointerEvents: 'none',
          }}
          accessibilityElementsHidden
          importantForAccessibility="no"
        >
          {slot}
        </Text>
      )}
    </View>
  );
}
